import React, { useMemo, useState } from "react";
import { ChevronRightIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { groupCoverage, hasPosition } from "../../utils/coverageMap";
import type { CoverageTotals } from "../../utils/coverageMap";
import CampaignRunCard, { OnAirPill } from "./CampaignRunCard";
import { formatPlayDuration } from "./campaignFormatting";
import type { MapScreen } from "../../schemas/campaignSchema";

interface CoverageBreakdownProps {
  screens: MapScreen[];
  /** The brand owns more creatives than the walk covers. */
  truncated: boolean;
  isLoading: boolean;
  isError: boolean;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const Count: React.FC<{ value: string | number; unit: string }> = ({
  value,
  unit,
}) => (
  <span className="whitespace-nowrap">
    <span className="font-semibold text-gray-700">{value}</span> {unit}
  </span>
);

/**
 * Every level of the accordion says the same three things, in the same order,
 * with the numbers carrying the weight so a column of rows can be compared
 * without reading the words between them.
 */
const Totals: React.FC<{ totals: CoverageTotals }> = ({ totals }) => (
  <span className="flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
    <Count
      value={totals.screenCount}
      unit={totals.screenCount === 1 ? "screen" : "screens"}
    />
    <Count value={totals.plays.toLocaleString()} unit="plays" />
    {totals.playDuration > 0 && (
      <Count value={formatPlayDuration(totals.playDuration)} unit="on screen" />
    )}
  </span>
);

/**
 * One row of the accordion, at whatever depth. `indent` is the only thing that
 * changes between a city, an area and a screen, so they cannot drift apart.
 */
const Row: React.FC<{
  open: boolean;
  onClick: () => void;
  indent: 0 | 1 | 2;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  right?: React.ReactNode;
}> = ({ open, onClick, indent, title, subtitle, right }) => (
  <button
    type="button"
    aria-expanded={open}
    onClick={onClick}
    className={`flex w-full items-center gap-2 border-b border-gray-100 py-2 pr-3 text-left transition hover:bg-gray-50 ${
      open ? "bg-gray-50" : "bg-white"
    } ${["pl-3", "pl-8", "pl-14"][indent]}`}
  >
    <ChevronRightIcon
      className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${
        open ? "rotate-90" : ""
      }`}
    />
    <span className="min-w-0 flex-1">
      <span
        className={`block truncate ${
          indent === 0
            ? "text-sm font-semibold text-gray-900"
            : "text-sm font-medium text-gray-800"
        }`}
      >
        {title}
      </span>
      {subtitle && (
        <span className="block truncate text-xs text-gray-500">{subtitle}</span>
      )}
    </span>
    {right}
  </button>
);

/**
 * Where the brand's campaigns run, as a drill-down: city, then the areas in it,
 * then the screens, then what each screen is playing.
 *
 * The nesting follows the data (`regions.cityId`), and one branch is open at a
 * time, so the panel stays short whether the brand has three screens or three
 * hundred.
 *
 * It reads the same unfiltered `brandScreenMap` payload the map pins rather
 * than the campaigns table: the table is one page at a time and its status is
 * the schedule window alone, so rolling it up would disagree with the map.
 */
const CoverageBreakdown: React.FC<CoverageBreakdownProps> = ({
  screens,
  truncated,
  isLoading,
  isError,
}) => {
  const [openCity, setOpenCity] = useState<string | null>(null);
  const [openRegion, setOpenRegion] = useState<string | null>(null);
  const [openScreen, setOpenScreen] = useState<string | null>(null);

  const cities = useMemo(() => groupCoverage(screens), [screens]);

  // A refresh can retire whatever was open; the next click then just works.
  const city = cities.find((c) => c.city === openCity) ?? null;
  const region = city?.regions.find((r) => r.region === openRegion) ?? null;

  const toggleCity = (name: string) => {
    setOpenCity(openCity === name ? null : name);
    setOpenRegion(null);
    setOpenScreen(null);
  };
  const toggleRegion = (name: string) => {
    setOpenRegion(openRegion === name ? null : name);
    setOpenScreen(null);
  };

  if (isLoading) {
    return (
      <p className="mb-4 py-6 text-center text-sm text-gray-500">
        Loading your coverage…
      </p>
    );
  }
  if (isError) {
    return (
      <p className="mb-4 py-6 text-center text-sm text-red-600">
        Your coverage could not be loaded. Try refreshing.
      </p>
    );
  }
  if (cities.length === 0) {
    return (
      <p className="mb-4 py-6 text-center text-sm text-gray-500">
        None of your campaigns are on a screen yet. Once your media house
        schedules them, the places they reach appear here.
      </p>
    );
  }

  return (
    <section
      aria-label="Coverage by city"
      className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
    >
      <h2 className="border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Where your campaigns run
      </h2>

      {truncated && (
        <p className="border-b border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          You have more campaigns than this can show at once. It covers your 500
          most recent.
        </p>
      )}

      {/*
        The accordion is the whole of the list view now, so it takes the rest of
        the window: the brand layout's main is 90vh, less the title row, the
        summary strip and this section's own header.
      */}
      <div className="max-h-[calc(90vh-12rem)] overflow-y-auto">
        {cities.map((cityRow) => (
          <div key={cityRow.city}>
            <Row
              open={cityRow.city === openCity}
              onClick={() => toggleCity(cityRow.city)}
              indent={0}
              title={cityRow.city}
              subtitle={<Totals totals={cityRow} />}
              right={cityRow.onAir > 0 ? <OnAirPill count={cityRow.onAir} /> : undefined}
            />

            {cityRow.city === openCity &&
              city?.regions.map((regionRow) => (
                <div key={regionRow.region}>
                  <Row
                    open={regionRow.region === openRegion}
                    onClick={() => toggleRegion(regionRow.region)}
                    indent={1}
                    title={regionRow.region}
                    subtitle={<Totals totals={regionRow} />}
                    right={
                      regionRow.onAir > 0 ? (
                        <OnAirPill count={regionRow.onAir} />
                      ) : undefined
                    }
                  />

                  {regionRow.region === openRegion &&
                    region?.screens.map((screen) => (
                      <div key={screen.screenId}>
                        <Row
                          open={screen.screenId === openScreen}
                          onClick={() =>
                            setOpenScreen(
                              openScreen === screen.screenId
                                ? null
                                : screen.screenId,
                            )
                          }
                          indent={2}
                          title={
                            <span className="flex items-center gap-2">
                              <span
                                className={`h-2 w-2 flex-shrink-0 rounded-full ${
                                  screen.screenOnline
                                    ? "bg-success-500"
                                    : "bg-gray-300"
                                }`}
                                title={
                                  screen.screenOnline
                                    ? "Screen online"
                                    : "Screen off"
                                }
                              />
                              {screen.screenName}
                              {!hasPosition(screen) && (
                                <span title="No map location yet, so this screen has no pin">
                                  <MapPinIcon className="h-3.5 w-3.5 text-gray-300" />
                                </span>
                              )}
                            </span>
                          }
                          subtitle={[
                            screen.restaurantName || screen.address,
                            plural(screen.campaigns.length, "campaign"),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          right={
                            screen.campaigns.some((c) => c.isPlayingNow) ? (
                              <OnAirPill />
                            ) : undefined
                          }
                        />

                        {screen.screenId === openScreen && (
                          <ul className="flex flex-col gap-1.5 border-b border-gray-100 bg-gray-50 px-3 py-2 pl-14">
                            {screen.campaigns.map((campaign) => (
                              <CampaignRunCard
                                key={campaign.creativeId}
                                campaign={campaign}
                              />
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                </div>
              ))}
          </div>
        ))}
      </div>
    </section>
  );
};

export default CoverageBreakdown;
